import {
  Component, ChangeDetectionStrategy, DestroyRef,
  OnInit, OnChanges, OnDestroy, SimpleChanges, Input, Output, EventEmitter, inject,
} from '@angular/core';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { interval } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { FirmaService } from '@uygulamalar/firma-yonetim/services/firma.service';
import { PdksDurumService } from '@uygulamalar/hmi-pano/services/pdks-durum.service';
import { PanoDurumService } from '@uygulamalar/hmi-pano/services/pano-durum.service';
import { PdksAylikOzetComponent } from '../pdks/pdks-aylik-ozet.component';
import { hmiAyarGetir } from '@uygulamalar/hmi-pano/config/hmi-parametre-setleri';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import type { IPdksAyarlar, PuantajGunDurum } from '@uygulamalar/firma-yonetim/interfaces/pdks.interface';
import { VARSAYILAN_PDKS_AYARLAR } from '@uygulamalar/firma-yonetim/interfaces/pdks.interface';

// ─── Tipler ──────────────────────────────────────────────────────────────────

export type PuantajKayitTur = 'giris' | 'cikis' | 'mola_baslangic' | 'mola_bitis';
export type GunTuru = 'calisma' | 'dinlenme' | 'tatil';
export type DialogTur = null | 'giris-saat' | 'cikis-saat' | 'mola-ekle' | 'mesai-carpani' | 'vardiya-sec' | 'calisma-suresi';

export interface IGunlukSatir {
  personelId: string;
  ad: string;
  soyad: string;
  sicilNo?: string;
  departmanAdi?: string;
  giris?: string;
  cikis?: string;
  molaDakika: number;
  toplamDakika: number;
  durum: 'aktif' | 'cikti' | 'molada' | 'gelmedi';
  hareketler: { tur: PuantajKayitTur; zaman: string }[];
}

export interface IVardiya {
  ismi: string;
  aciklama?: string;
  sureDakika: number;
  molalar?: { ismi: string; baslangic: string; bitis: string }[];
}

interface ITakvimGunVeri {
  toplamDk: number;
  durum: 'aktif' | 'cikti' | 'molada' | 'gelmedi';
  fazlaMesaiVar: boolean;
  girisStr?: string;
  cikisStr?: string;
  notVar?: boolean;
  resmiTatil?: boolean;
  raporlu?: boolean;
  izinli?: boolean;
  kayitSayisi?: number;        // çoklu giriş/çıkış sayısı
  gecGelme?: boolean;          // tolerans aşımı
  renk?: string;               // takvim hücre renk kodu
  gunDurumu?: PuantajGunDurum; // detaylı durum
}

interface ITakvimHucre {
  tarih: Date;
  gunNo: number;
  ayIci: boolean;
  bugun: boolean;
  secili: boolean;
  haftaSonu: boolean;
  veri?: ITakvimGunVeri;
}

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

function zamanStr(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}
function zamanStrSaniye(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
}
function dakikaFark(a: string, b: string): number {
  return Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000));
}
function dk2Saat(dk: number): string {
  if (dk <= 0) return '—';
  const h = Math.floor(dk / 60);
  const m = dk % 60;
  return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
}
function dk2SaatKisa(dk: number): string {
  if (dk <= 0) return '';
  const h = Math.floor(dk / 60);
  const m = dk % 60;
  return h > 0 ? (m > 0 ? `${h}s${m}d` : `${h}s`) : `${m}d`;
}
function sureStr(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sn = s % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sn.toString().padStart(2, '0')}`;
}
/**
 * Hareketleri çözümle — çoklu giriş/çıkış desteği.
 * İlk giriş ve son çıkışı kullanır, tüm molaları toplar.
 * Gece vardiyası: giriş saati >= geceVardiyasiSaati ise ertesi günün çıkışını da kapsar.
 */
function hareketleriCoz(hareketler: { tur: PuantajKayitTur; zaman: string }[], coklu = false): Omit<IGunlukSatir, 'personelId' | 'ad' | 'soyad' | 'sicilNo' | 'departmanAdi' | 'hareketler'> & { kayitSayisi: number } {
  const sorted = [...hareketler].sort((a, b) => a.zaman.localeCompare(b.zaman));
  let giris: string | undefined;
  let cikis: string | undefined;
  let molaDakika = 0;
  let molaBaslangic: string | undefined;
  let kayitSayisi = 0;

  if (coklu) {
    // Çoklu mod: her giriş/çıkış çiftini ayrı say, topla
    let toplamDakika = 0;
    let aktifGiris: string | undefined;
    for (const h of sorted) {
      if (h.tur === 'giris') {
        if (!giris) giris = h.zaman; // ilk giriş
        aktifGiris = h.zaman;
        kayitSayisi++;
      }
      if (h.tur === 'cikis' && aktifGiris) {
        cikis = h.zaman;
        toplamDakika += dakikaFark(aktifGiris, h.zaman);
        aktifGiris = undefined;
      }
      if (h.tur === 'mola_baslangic') molaBaslangic = h.zaman;
      if (h.tur === 'mola_bitis' && molaBaslangic) {
        molaDakika += dakikaFark(molaBaslangic, h.zaman);
        molaBaslangic = undefined;
      }
    }
    const molaSuruyor = !!molaBaslangic;
    let durum: IGunlukSatir['durum'] = 'gelmedi';
    if (giris && !cikis) durum = molaSuruyor ? 'molada' : 'aktif';
    else if (giris && cikis) durum = aktifGiris ? 'aktif' : 'cikti';
    toplamDakika = Math.max(0, toplamDakika - molaDakika);
    return { giris, cikis, molaDakika, toplamDakika, durum, kayitSayisi: Math.max(1, kayitSayisi) };
  }

  // Tek kayıt modu (eski davranış)
  for (const h of sorted) {
    if (h.tur === 'giris' && !giris) giris = h.zaman;
    if (h.tur === 'cikis') cikis = h.zaman;
    if (h.tur === 'mola_baslangic') molaBaslangic = h.zaman;
    if (h.tur === 'mola_bitis' && molaBaslangic) {
      molaDakika += dakikaFark(molaBaslangic, h.zaman);
      molaBaslangic = undefined;
    }
  }
  const molaSuruyor = !!molaBaslangic;
  let durum: IGunlukSatir['durum'] = 'gelmedi';
  if (giris && !cikis) durum = molaSuruyor ? 'molada' : 'aktif';
  else if (giris && cikis) durum = 'cikti';
  const toplamBrut = giris && cikis ? dakikaFark(giris, cikis) : 0;
  const toplamDakika = Math.max(0, toplamBrut - molaDakika);
  return { giris, cikis, molaDakika, toplamDakika, durum, kayitSayisi: 1 };
}

const GUN_ISIMLERI = ['Pazar', 'Pazartesi', 'Sali', 'Carsamba', 'Persembe', 'Cuma', 'Cumartesi'];
const GUN_KISA = ['Paz', 'Pzt', 'Sal', 'Car', 'Per', 'Cum', 'Cmt'];
const AY_ISIMLERI = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];

const VARSAYILAN_VARDIYALAR: IVardiya[] = [
  { ismi: 'Surekli Gunduz', aciklama: '08:00 – 17:00', sureDakika: 540, molalar: [{ ismi: 'Ogle', baslangic: '12:00', bitis: '13:00' }] },
  { ismi: 'Sabah', aciklama: '06:00 – 14:00', sureDakika: 480, molalar: [{ ismi: 'Ara', baslangic: '10:00', bitis: '10:30' }] },
  { ismi: 'Aksam', aciklama: '14:00 – 22:00', sureDakika: 480, molalar: [{ ismi: 'Ara', baslangic: '18:00', bitis: '18:30' }] },
  { ismi: 'Gece', aciklama: '22:00 – 06:00', sureDakika: 480, molalar: [{ ismi: 'Gece', baslangic: '02:00', bitis: '02:30' }] },
];

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'hmi-puantaj',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, NzButtonModule, NzTooltipModule, NzDividerModule, PdksAylikOzetComponent],
  templateUrl: './puantaj.component.html',
  styleUrl: './puantaj.component.less',
})
export class HmiPuantajComponent extends FikoYTKokBilesenComponent implements OnInit, OnChanges, OnDestroy {
  constructor() { super(); }
  private firmaService = inject(FirmaService);
  pdks = inject(PdksDurumService);
  private panoDurum = inject(PanoDurumService);
  private destroyRef = inject(DestroyRef);

  @Input() aktifPersonel: any = null;
  @Input() kisiYetkileri: string[] = [];
  @Input() firmaId = '';
  @Output() geriDonTiklandi = new EventEmitter<void>();

  aktifSatir: IGunlukSatir | null = null;
  islemYapiliyor = false;
  yukleniyor = false;
  aktifDialog: DialogTur = null;

  // Secili gun
  seciliGun = new Date();
  takvimAy = new Date().getMonth();
  takvimYil = new Date().getFullYear();
  takvimVerileri: Record<string, ITakvimGunVeri> = {};

  // Gun turu
  gunTuru: GunTuru = 'calisma';
  readonly gunTurleri = [
    { key: 'calisma' as GunTuru, ikon: '🏭', etiket: 'Calisma' },
    { key: 'dinlenme' as GunTuru, ikon: '🌙', etiket: 'Dinlenme' },
    { key: 'tatil' as GunTuru, ikon: '🎉', etiket: 'Tatil' },
  ];

  // Bayraklar
  servisGecikmesi = false; resmiTatil = false; raporlu = false; gunlukIzinli = false; yillikIzinli = false;
  readonly bayraklar = [
    { key: 'servis', ikon: '🚌', etiket: 'Servis Gecikmesi', kisaEtiket: 'Servis' },
    { key: 'tatil', ikon: '🚩', etiket: 'Resmi Tatil', kisaEtiket: 'Tatil' },
    { key: 'rapor', ikon: '💔', etiket: 'Raporlu', kisaEtiket: 'Rapor' },
    { key: 'gunluk', ikon: '🏨', etiket: 'Gunluk Izin', kisaEtiket: 'G.Izin' },
    { key: 'yillik', ikon: '🏖️', etiket: 'Yillik Izin', kisaEtiket: 'Y.Izin' },
  ];

  // Canli sayac
  canliCalismaStr = '00:00:00'; canliMolaStr = '0dk'; normalMesaiStr = '00:00:00'; fazlaMesaiStr = '00:00:00'; fazlaMesaiMs = 0;
  private _timerRef: any = null;

  // Vardiya
  vardiyalar: IVardiya[] = [...VARSAYILAN_VARDIYALAR];
  aktifVardiyaIdx = 0;

  // Mesai carpani
  mesaiOrani = 100; dlgMesaiOrani = 100;

  // Calisma suresi
  calismaSuresiOverrideDk = 0; dlgCalismaSuresiDk = 540;

  // Dialog saat degerleri
  dlgGirisSaat = ''; dlgCikisSaat = '';

  // Mola dialog
  yeniMolaIsmi = ''; yeniMolaBaslangic = ''; yeniMolaBitis = '';

  private cihazId = '';

  readonly zamanStr = zamanStr;
  readonly zamanStrSn = zamanStrSaniye;
  readonly dk2Saat = dk2Saat;
  readonly dk2SaatKisa = dk2SaatKisa;
  readonly AY_ISIMLERI_T = AY_ISIMLERI;
  readonly HAFTA_KISA = ['Pzt', 'Sal', 'Car', 'Per', 'Cum', 'Cmt', 'Paz'];

  // ═══════════════════ GETTERS ═══════════════════

  get seciliGunBugunMu(): boolean {
    const b = new Date();
    return this.seciliGun.getDate() === b.getDate() && this.seciliGun.getMonth() === b.getMonth() && this.seciliGun.getFullYear() === b.getFullYear();
  }
  get kayitDuzenleyebilir(): boolean { return this.panoDurum.superYoneticiMi; }

  private duzenlemeYetkisiKontrol(): boolean {
    if (this.kayitDuzenleyebilir) return true;
    this.nzMessage.warning('Puantaj kayıtlarını yalnızca süper yönetici düzenleyebilir');
    return false;
  }
  get seciliGunAdi(): string { return GUN_ISIMLERI[this.seciliGun.getDay()]; }
  get seciliGunGorunum(): string {
    const d = this.seciliGun;
    return `${d.getDate()} ${AY_ISIMLERI[d.getMonth()]} ${d.getFullYear()}`;
  }
  get seciliGunKey(): string {
    const d = this.seciliGun;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  get bugunAy(): number { return new Date().getMonth(); }
  get bugunYil(): number { return new Date().getFullYear(); }
  get aktifVardiyaIsmi(): string { return this.vardiyalar[this.aktifVardiyaIdx]?.ismi || 'Seciniz'; }
  get aktifVardiyaSureDk(): number { return this.vardiyalar[this.aktifVardiyaIdx]?.sureDakika || 480; }
  private get vardiyaSureMs(): number {
    if (this.calismaSuresiOverrideDk > 0) return this.calismaSuresiOverrideDk * 60_000;
    return this.aktifVardiyaSureDk * 60_000;
  }
  get molaCiftleri(): { baslangic: string; bitis?: string; sure: number; ismi?: string }[] {
    if (!this.aktifSatir?.hareketler) return [];
    const baslangiclar = this.aktifSatir.hareketler.filter(h => h.tur === 'mola_baslangic').sort((a, b) => a.zaman.localeCompare(b.zaman));
    const bitisler = this.aktifSatir.hareketler.filter(h => h.tur === 'mola_bitis').sort((a, b) => a.zaman.localeCompare(b.zaman));
    return baslangiclar.map((b, i) => ({ baslangic: b.zaman, bitis: bitisler[i]?.zaman, sure: bitisler[i] ? dakikaFark(b.zaman, bitisler[i].zaman) : 0 }));
  }
  get yeniMolaSure(): string {
    if (!this.yeniMolaBaslangic || !this.yeniMolaBitis) return '';
    const a = new Date(`${this.seciliGunKey}T${this.yeniMolaBaslangic}:00`);
    const b = new Date(`${this.seciliGunKey}T${this.yeniMolaBitis}:00`);
    return dk2Saat(Math.max(0, Math.round((b.getTime() - a.getTime()) / 60000)));
  }

  gunKisaIsim(tarih: Date): string { return GUN_KISA[tarih.getDay()]; }

  // ─── Progress Ring ─────────────────────────────────────────────────────────
  private readonly RING_R = 34;
  private readonly RING_CEVRE = 2 * Math.PI * 34; // ~213.6
  get ringCevre(): string { return `${this.RING_CEVRE}`; }
  get tamamlanmaYuzde(): number {
    const gunluk = this.pdks.pdksAyarlar().gunlukCalismaSaati * 60;
    if (gunluk <= 0 || !this.aktifSatir) return 0;
    return Math.min(100, Math.round((this.aktifSatir.toplamDakika / gunluk) * 100));
  }
  get ringOffset(): string { return `${this.RING_CEVRE * (1 - this.tamamlanmaYuzde / 100)}`; }

  // ─── Gece Vardiyası Tespiti ────────────────────────────────────────────────
  get geceVardiyasiMi(): boolean {
    const v = this.vardiyalar[this.aktifVardiyaIdx];
    if (!v?.aciklama) return false;
    const basSaat = parseInt(v.aciklama.split('–')[0]?.trim()?.split(':')[0] || '0');
    return basSaat >= this.pdks.pdksAyarlar().geceVardiyasiGunAtamaSaati;
  }

  // ─── Aktif Mesai Çarpanı ──────────────────────────────────────────────────
  get aktifCarpan() { return this.pdks.aktifMesaiCarpani(); }

  // ─── PDKS Yenileme ────────────────────────────────────────────────────────
  async pdksYenilendi() { await this.yukle(); }

  get aylikOzet(): { calismaSuresi: string; fazlaMesai: string; cGunuMesai: string; dGunuMesai: string; hTatilMesai: string; rTatilMesai: string; izinliSure: string; raporluSure: string; yillikIzin: string } {
    let toplamDk = 0; let fazlaDk = 0;
    let cGunuDk = 0; let dGunuDk = 0; let hTatilDk = 0; let rTatilDk = 0;
    const vrdDk = this.aktifVardiyaSureDk;
    const ayarlar = this.pdks.pdksAyarlar();
    for (const key of Object.keys(this.takvimVerileri)) {
      const v = this.takvimVerileri[key];
      toplamDk += v.toplamDk;
      if (v.toplamDk > vrdDk) {
        const fazla = v.toplamDk - vrdDk;
        fazlaDk += fazla;
        // Gün tipine göre mesai dağılımı
        const tarih = new Date(key);
        const gun = tarih.getDay();
        const tatilMi = ayarlar.resmiTatiller.some(t => t.tarih === key);
        if (tatilMi) rTatilDk += fazla;
        else if (gun === 6) cGunuDk += fazla;
        else if (gun === 0) dGunuDk += fazla;
        else hTatilDk += fazla;
      }
    }
    const f = (dk: number) => dk > 0 ? `${Math.floor(dk / 60)}s ${dk % 60}dk` : '';
    return {
      calismaSuresi: toplamDk > 0 ? `${Math.floor(toplamDk / 60)}s ${toplamDk % 60}dk` : '0s 0dk',
      fazlaMesai: f(fazlaDk),
      cGunuMesai: f(cGunuDk), dGunuMesai: f(dGunuDk),
      hTatilMesai: f(hTatilDk), rTatilMesai: f(rTatilDk),
      izinliSure: '', raporluSure: '', yillikIzin: '',
    };
  }

  // ═══════════════════ TAKVİM GETTER ═══════════════════

  get takvimHucreleri(): ITakvimHucre[] {
    const ayBaslangic = new Date(this.takvimYil, this.takvimAy, 1);
    const haftaGunu = ayBaslangic.getDay();
    const pazartesiFark = haftaGunu === 0 ? -6 : 1 - haftaGunu;
    const ilkGun = new Date(ayBaslangic); ilkGun.setDate(ilkGun.getDate() + pazartesiFark);
    const bugun = new Date(); bugun.setHours(0, 0, 0, 0);
    const secNorm = new Date(this.seciliGun); secNorm.setHours(0, 0, 0, 0);
    const hucreler: ITakvimHucre[] = [];
    for (let i = 0; i < 42; i++) {
      const tarih = new Date(ilkGun); tarih.setDate(tarih.getDate() + i);
      const key = `${tarih.getFullYear()}-${String(tarih.getMonth() + 1).padStart(2, '0')}-${String(tarih.getDate()).padStart(2, '0')}`;
      hucreler.push({
        tarih, gunNo: tarih.getDate(), ayIci: tarih.getMonth() === this.takvimAy,
        bugun: tarih.getTime() === bugun.getTime(), secili: tarih.getTime() === secNorm.getTime(),
        haftaSonu: tarih.getDay() === 0 || tarih.getDay() === 6, veri: this.takvimVerileri[key],
      });
    }
    return hucreler;
  }

  // ═══════════════════ LIFECYCLE ═══════════════════

  ngOnInit() {
    if (!this.firmaId) this.firmaId = hmiAyarGetir<string>('firmaId', '');
    // Router-outlet modunda @Input gelmez — PanoDurumService'ten oku
    if (!this.aktifPersonel) this.aktifPersonel = this.panoDurum.sonPersonel();
    if (!this.kisiYetkileri?.length) this.kisiYetkileri = this.panoDurum.kisiYetkileri();
    this.cihazId = localStorage.getItem('fikoyt_cihaz_kimlik') || '';
    // PdksDurumService senkronizasyonu
    this.pdks.firmaId.set(this.firmaId);
    this.pdks.cihazId.set(this.cihazId);
    this.pdks.pdksAyarlarYukle(this.firmaId);
    // DB'den vardiyaları yükle
    this.dbVardiyalariYukle();
    if (this.aktifPersonel) this.ilkYukle();
    this._timerRef = setInterval(() => { if (this.seciliGunBugunMu) this.canliSayacGuncelle(); }, 1000);
    interval(30_000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.aktifPersonel && this.seciliGunBugunMu) this.yukle(true);
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['aktifPersonel'] && !changes['aktifPersonel'].firstChange && this.aktifPersonel) this.ilkYukle();
  }
  ngOnDestroy() { super.ngOnDestroy(); if (this._timerRef) clearInterval(this._timerRef); }

  private async ilkYukle() {
    // PdksDurumService'e personeli bildir
    this.pdks.aktifPersonel.set(this.aktifPersonel);
    await this.yukle(); await this.takvimYukle(); this.CD.detectChanges();
  }

  private async dbVardiyalariYukle() {
    if (!this.firmaId) return;
    try {
      const dbVardiyalar = await this.firmaService.vardiyaListesi(this.firmaId);
      if (dbVardiyalar?.length > 0) {
        this.vardiyalar = dbVardiyalar.map(v => ({
          ismi: v.vardiyaAdi,
          aciklama: `${v.baslangicSaati} – ${v.bitisSaati}`,
          sureDakika: this.vardiyaSureDkHesapla(v.baslangicSaati, v.bitisSaati),
          molalar: (v.molaSureleri || []).map(m => ({ ismi: m.aciklama, baslangic: m.baslangic, bitis: m.bitis })),
        }));
        if (this.vardiyalar.length > 0 && this.aktifVardiyaIdx >= this.vardiyalar.length) {
          this.aktifVardiyaIdx = 0;
        }
        this.CD.detectChanges();
      }
    } catch { /* DB vardiyaları yüklenemezse varsayılanları kullan */ }
  }

  private vardiyaSureDkHesapla(bas: string, bit: string): number {
    const [bh, bm] = bas.split(':').map(Number);
    const [sh, sm] = bit.split(':').map(Number);
    let dk = (sh * 60 + sm) - (bh * 60 + bm);
    if (dk <= 0) dk += 24 * 60; // gece vardiyası
    return dk;
  }
  geriDon() {
    this.geriDonTiklandi.emit();
    // Router-outlet modunda: personel shell ana sayfasına dön
    this.router.navigate(['..'], { relativeTo: this.route });
  }
  durumYazi(d: string): string {
    switch (d) { case 'aktif': return 'Aktif'; case 'cikti': return 'Cikti'; case 'molada': return 'Molada'; default: return 'Gelmedi'; }
  }

  // ═══════════════════ KART EKLE ═══════════════════

  async kartEkle() {
    if (!this.duzenlemeYetkisiKontrol()) return;
    if (!this.aktifPersonel || this.islemYapiliyor) return;
    this.islemYapiliyor = true;
    try {
      const iso = new Date(`${this.seciliGunKey}T08:00:00`).toISOString();
      await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: this.aktifPersonel.kartUid || '', cihazId: this.cihazId, tur: 'giris', zaman: iso, onayDurumu: 'manuel' } as any);
      this.nzMessage.success('Kart olusturuldu');
      await this.yukle();
    } catch { this.nzMessage.error('Kart olusturulamadi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }

  // ═══════════════════ DIALOG ═══════════════════

  dialogAc(tur: DialogTur) {
    if (!this.duzenlemeYetkisiKontrol()) return;
    if (tur === 'giris-saat') {
      this.dlgGirisSaat = this.aktifSatir?.giris ? new Date(this.aktifSatir.giris).toTimeString().substring(0, 8) : '';
    } else if (tur === 'cikis-saat') {
      this.dlgCikisSaat = this.aktifSatir?.cikis ? new Date(this.aktifSatir.cikis).toTimeString().substring(0, 8) : '';
    } else if (tur === 'mola-ekle') {
      this.yeniMolaIsmi = ''; this.yeniMolaBaslangic = ''; this.yeniMolaBitis = '';
    } else if (tur === 'mesai-carpani') {
      this.dlgMesaiOrani = this.mesaiOrani;
    } else if (tur === 'calisma-suresi') {
      this.dlgCalismaSuresiDk = this.calismaSuresiOverrideDk > 0 ? this.calismaSuresiOverrideDk : this.aktifVardiyaSureDk;
    }
    this.aktifDialog = tur; this.CD.detectChanges();
  }
  dialogKapat() { this.aktifDialog = null; this.CD.detectChanges(); }

  dlgGirisSimdi() { const n = new Date(); this.dlgGirisSaat = `${n.getHours().toString().padStart(2, '0')}:${n.getMinutes().toString().padStart(2, '0')}:${n.getSeconds().toString().padStart(2, '0')}`; }
  dlgGirisNormal() { this.dlgGirisSaat = '08:00:00'; }
  async dlgGirisKaydet() {
    if (!this.duzenlemeYetkisiKontrol()) return;
    if (!this.aktifPersonel || !this.dlgGirisSaat) return;
    this.islemYapiliyor = true;
    try {
      const iso = new Date(`${this.seciliGunKey}T${this.dlgGirisSaat}`).toISOString();
      if (this.aktifSatir?.giris) await this.firmaService.puantajDuzenle(this.firmaId, this.aktifPersonel._id, 'giris', this.aktifSatir.giris, iso, 'HMI saat secici');
      else await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: this.aktifPersonel.kartUid || '', cihazId: this.cihazId, tur: 'giris', zaman: iso, onayDurumu: 'manuel' } as any);
      this.nzMessage.success('Giris saati kaydedildi'); this.dialogKapat(); await this.yukle();
    } catch { this.nzMessage.error('Giris kaydi basarisiz'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async dlgGirisSil() { this.nzMessage.info('Giris silme henuz desteklenmiyor'); this.dialogKapat(); }

  dlgCikisSimdi() { const n = new Date(); this.dlgCikisSaat = `${n.getHours().toString().padStart(2, '0')}:${n.getMinutes().toString().padStart(2, '0')}:${n.getSeconds().toString().padStart(2, '0')}`; }
  dlgCikisNormal() { this.dlgCikisSaat = '17:00:00'; }
  async dlgCikisKaydet() {
    if (!this.duzenlemeYetkisiKontrol()) return;
    if (!this.aktifPersonel || !this.dlgCikisSaat) return;
    this.islemYapiliyor = true;
    try {
      const iso = new Date(`${this.seciliGunKey}T${this.dlgCikisSaat}`).toISOString();
      if (this.aktifSatir?.cikis) await this.firmaService.puantajDuzenle(this.firmaId, this.aktifPersonel._id, 'cikis', this.aktifSatir.cikis, iso, 'HMI saat secici');
      else await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: this.aktifPersonel.kartUid || '', cihazId: this.cihazId, tur: 'cikis', zaman: iso, onayDurumu: 'manuel' } as any);
      this.nzMessage.success('Cikis saati kaydedildi'); this.dialogKapat(); await this.yukle();
    } catch { this.nzMessage.error('Cikis kaydi basarisiz'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async dlgCikisSil() { this.nzMessage.info('Cikis silme henuz desteklenmiyor'); this.dialogKapat(); }

  async dlgMolaEkle() {
    if (!this.duzenlemeYetkisiKontrol()) return;
    if (!this.aktifPersonel || !this.yeniMolaBaslangic) return;
    this.islemYapiliyor = true;
    try {
      const t = this.seciliGunKey;
      const isoBasla = new Date(`${t}T${this.yeniMolaBaslangic}:00`).toISOString();
      await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: '', cihazId: this.cihazId, tur: 'mola_baslangic', zaman: isoBasla, onayDurumu: 'manuel' } as any);
      if (this.yeniMolaBitis) {
        const isoBitis = new Date(`${t}T${this.yeniMolaBitis}:00`).toISOString();
        await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: '', cihazId: this.cihazId, tur: 'mola_bitis', zaman: isoBitis, onayDurumu: 'manuel' } as any);
      }
      this.nzMessage.success('Mola eklendi'); this.yeniMolaIsmi = ''; this.yeniMolaBaslangic = ''; this.yeniMolaBitis = ''; await this.yukle();
    } catch { this.nzMessage.error('Mola eklenemedi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async dlgGecerliMolalar() {
    if (!this.duzenlemeYetkisiKontrol()) return;
    const v = this.vardiyalar[this.aktifVardiyaIdx];
    if (!v?.molalar?.length || !this.aktifPersonel) { this.nzMessage.info('Bu vardiyada tanimli mola yok'); return; }
    this.islemYapiliyor = true;
    try {
      const t = this.seciliGunKey;
      for (const m of v.molalar) {
        await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: '', cihazId: this.cihazId, tur: 'mola_baslangic', zaman: new Date(`${t}T${m.baslangic}:00`).toISOString(), onayDurumu: 'manuel' } as any);
        await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: '', cihazId: this.cihazId, tur: 'mola_bitis', zaman: new Date(`${t}T${m.bitis}:00`).toISOString(), onayDurumu: 'manuel' } as any);
      }
      this.nzMessage.success(`${v.molalar.length} mola eklendi`); await this.yukle();
    } catch { this.nzMessage.error('Molalar eklenemedi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  dlgMolalarTemizle() { this.nzMessage.info('Mola temizleme henuz desteklenmiyor'); this.dialogKapat(); }

  dlgMesaiKaydet() { if (!this.duzenlemeYetkisiKontrol()) return; this.mesaiOrani = this.dlgMesaiOrani; this.canliSayacGuncelle(); this.nzMessage.success(`Mesai orani %${this.mesaiOrani}`); this.dialogKapat(); }
  vardiyaSec(idx: number) { if (!this.duzenlemeYetkisiKontrol()) return; this.aktifVardiyaIdx = idx; this.canliSayacGuncelle(); this.nzMessage.success(`${this.vardiyalar[idx].ismi} secildi`); this.dialogKapat(); }
  dlgCalismaSuresiKaydet() { if (!this.duzenlemeYetkisiKontrol()) return; this.calismaSuresiOverrideDk = this.dlgCalismaSuresiDk; this.canliSayacGuncelle(); this.nzMessage.success(`Sure ${dk2Saat(this.dlgCalismaSuresiDk)}`); this.dialogKapat(); }

  // ═══════════════════ CANLI SAYAC ═══════════════════

  private canliSayacGuncelle() {
    const s = this.aktifSatir;
    if (!s?.giris) { this.canliCalismaStr = '00:00:00'; this.normalMesaiStr = '00:00:00'; this.fazlaMesaiStr = '00:00:00'; this.fazlaMesaiMs = 0; this.canliMolaStr = '0dk'; this.CD.markForCheck(); return; }
    const now = Date.now();
    const girisMs = new Date(s.giris).getTime();
    const cikisMs = s.cikis ? new Date(s.cikis).getTime() : (this.seciliGunBugunMu ? now : girisMs);
    const molaMs = s.molaDakika * 60_000;
    let canliMolaMs = molaMs;
    if (s.durum === 'molada' && this.seciliGunBugunMu) {
      const son = [...s.hareketler].reverse().find(h => h.tur === 'mola_baslangic');
      if (son) canliMolaMs += now - new Date(son.zaman).getTime();
      this.canliMolaStr = dk2Saat(Math.floor(canliMolaMs / 60_000));
    } else { this.canliMolaStr = molaMs > 0 ? dk2Saat(s.molaDakika) : '0dk'; }
    const calismaMs = Math.max(0, cikisMs - girisMs - canliMolaMs);
    this.canliCalismaStr = sureStr(calismaMs);
    const normalMs = Math.min(calismaMs, this.vardiyaSureMs);
    this.normalMesaiStr = sureStr(normalMs);
    this.fazlaMesaiMs = Math.max(0, calismaMs - this.vardiyaSureMs);
    this.fazlaMesaiStr = sureStr(this.fazlaMesaiMs);
    this.CD.markForCheck();
  }

  // ═══════════════════ GUN TURU & BAYRAKLAR ═══════════════════

  gunTuruDegistir(tur: GunTuru) { if (!this.duzenlemeYetkisiKontrol()) return; this.gunTuru = tur; }
  bayrakDegeri(key: string): boolean {
    switch (key) { case 'servis': return this.servisGecikmesi; case 'tatil': return this.resmiTatil; case 'rapor': return this.raporlu; case 'gunluk': return this.gunlukIzinli; case 'yillik': return this.yillikIzinli; default: return false; }
  }
  bayrakToggle(key: string) {
    if (!this.duzenlemeYetkisiKontrol()) return;
    switch (key) {
      case 'servis': this.servisGecikmesi = !this.servisGecikmesi; break;
      case 'tatil': this.resmiTatil = !this.resmiTatil; break;
      case 'rapor': this.raporlu = !this.raporlu; if (this.raporlu) { this.gunlukIzinli = false; this.yillikIzinli = false; } break;
      case 'gunluk': this.gunlukIzinli = !this.gunlukIzinli; if (this.gunlukIzinli) { this.raporlu = false; this.yillikIzinli = false; } break;
      case 'yillik': this.yillikIzinli = !this.yillikIzinli; if (this.yillikIzinli) { this.raporlu = false; this.gunlukIzinli = false; } break;
    }
    this.CD.markForCheck();
  }

  // ═══════════════════ AKSİYONLAR ═══════════════════

  async girisYap() {
    if (!this.aktifPersonel || this.islemYapiliyor) return;
    this.islemYapiliyor = true;
    try { await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: this.aktifPersonel.kartUid || '', cihazId: this.cihazId, tur: 'giris' } as any); this.nzMessage.success('Giris kaydedildi'); await this.yukle(); }
    catch { this.nzMessage.error('Giris kaydi olusturulamadi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async cikisYap() {
    if (!this.aktifPersonel || this.islemYapiliyor) return;
    this.islemYapiliyor = true;
    try { await this.firmaService.girisCikisKaydet(this.firmaId, { personelId: this.aktifPersonel._id, kartUid: this.aktifPersonel.kartUid || '', cihazId: this.cihazId, tur: 'cikis' } as any); this.nzMessage.success('Cikis kaydedildi'); await this.yukle(); }
    catch { this.nzMessage.error('Cikis kaydi olusturulamadi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async molaBaslat() {
    if (!this.aktifPersonel || this.islemYapiliyor) return;
    this.islemYapiliyor = true;
    try { await this.firmaService.molaKaydet(this.firmaId, this.aktifPersonel._id, this.aktifPersonel.kartUid || '', this.cihazId, 'mola_baslangic'); this.nzMessage.info('Mola basladi'); await this.yukle(); }
    catch { this.nzMessage.error('Mola kaydi olusturulamadi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }
  async molaBitir() {
    if (!this.aktifPersonel || this.islemYapiliyor) return;
    this.islemYapiliyor = true;
    try { await this.firmaService.molaKaydet(this.firmaId, this.aktifPersonel._id, this.aktifPersonel.kartUid || '', this.cihazId, 'mola_bitis'); this.nzMessage.success('Mola bitti'); await this.yukle(); }
    catch { this.nzMessage.error('Mola bitis kaydi olusturulamadi'); }
    finally { this.islemYapiliyor = false; this.CD.detectChanges(); }
  }

  // ═══════════════════ YUKLEME ═══════════════════

  async yukle(sessiz = false) {
    if (!this.aktifPersonel) return;
    if (!sessiz) { this.yukleniyor = true; this.CD.detectChanges(); }
    try {
      const gun = new Date(this.seciliGun); gun.setHours(0, 0, 0, 0);
      const ertesi = new Date(gun); ertesi.setDate(ertesi.getDate() + 1);
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = { aggregate: [
        { $match: { vt: 'FikoYT.sistem.giris_cikis', firmaId: this.firmaId, personelId: this.aktifPersonel._id, zaman: { $gte: gun.toISOString(), $lt: ertesi.toISOString() } } },
        { $sort: { zaman: 1 } },
        { $group: { _id: null, hareketler: { $push: { tur: '$tur', zaman: '$zaman' } } } },
      ] };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || [];
      const coklu = this.pdks.pdksAyarlar().cokluGirisCikis;
      if (ham.length > 0 && ham[0].hareketler?.length > 0) {
        const cozum = hareketleriCoz(ham[0].hareketler, coklu);
        this.aktifSatir = { personelId: this.aktifPersonel._id, ad: this.aktifPersonel.ad || '', soyad: this.aktifPersonel.soyad || '', hareketler: ham[0].hareketler, giris: cozum.giris, cikis: cozum.cikis, molaDakika: cozum.molaDakika, toplamDakika: cozum.toplamDakika, durum: cozum.durum };
      } else { this.aktifSatir = null; }
      this.takvimGunGuncelle();
      this.canliSayacGuncelle();
    } catch { if (!sessiz) this.nzMessage.error('Puantaj verileri yuklenemedi'); }
    finally { this.yukleniyor = false; this.CD.detectChanges(); }
  }

  // ═══════════════════ TAKVİM ═══════════════════

  async takvimYukle() {
    if (!this.aktifPersonel || !this.firmaId) return;
    const ayBas = new Date(this.takvimYil, this.takvimAy, 1); ayBas.setHours(0, 0, 0, 0);
    const aySon = new Date(this.takvimYil, this.takvimAy + 1, 1); aySon.setHours(0, 0, 0, 0);
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = { aggregate: [
        { $match: { vt: 'FikoYT.sistem.giris_cikis', firmaId: this.firmaId, personelId: this.aktifPersonel._id, zaman: { $gte: ayBas.toISOString(), $lt: aySon.toISOString() } } },
        { $sort: { zaman: 1 } },
        { $group: { _id: { $dateToString: { format: '%Y-%m-%d', date: { $toDate: '$zaman' } } }, hareketler: { $push: { tur: '$tur', zaman: '$zaman' } } } },
      ] };
      await istek.isteSync();
      const ham: any[] = istek.cevap?.sonuc || [];
      const veriler: Record<string, ITakvimGunVeri> = {};
      const ayarlar = this.pdks.pdksAyarlar();
      const coklu = ayarlar.cokluGirisCikis;
      for (const r of ham) {
        const c = hareketleriCoz(r.hareketler || [], coklu);
        const vrdDk = this.aktifVardiyaSureDk;
        const fazlaMesaiVar = c.toplamDakika > vrdDk;
        // Renk kodu tespiti
        let renk = '#52c41a'; // yeşil: normal
        const tarihObj = new Date(r._id);
        const tatilMi = ayarlar.resmiTatiller.some(t => t.tarih === r._id);
        const haftaSonuMu = tarihObj.getDay() === 0 || tarihObj.getDay() === 6;
        if (tatilMi) renk = '#722ed1';       // mor
        else if (fazlaMesaiVar) renk = '#fa8c16'; // turuncu (fazla mesai)
        else if (haftaSonuMu && c.toplamDakika > 0) renk = '#faad14'; // sarı
        // Geç gelme kontrolü
        let gecGelme = false;
        if (c.giris) {
          const girisSaat = new Date(c.giris).getHours() * 60 + new Date(c.giris).getMinutes();
          const vardiyaBas = this.vardiyalar[this.aktifVardiyaIdx];
          if (vardiyaBas?.aciklama) {
            const basSaat = parseInt(vardiyaBas.aciklama.split('–')[0]?.trim()?.split(':')[0] || '8');
            const basDk = parseInt(vardiyaBas.aciklama.split('–')[0]?.trim()?.split(':')[1] || '0');
            if (girisSaat - (basSaat * 60 + basDk) > ayarlar.girisToleransDk) {
              gecGelme = true;
              renk = '#faad14'; // sarı: geç gelme
            }
          }
        }
        veriler[r._id] = { toplamDk: c.toplamDakika, durum: c.durum, fazlaMesaiVar, girisStr: c.giris ? zamanStr(c.giris) : undefined, cikisStr: c.cikis ? zamanStr(c.cikis) : undefined, kayitSayisi: c.kayitSayisi, gecGelme, renk };
      }
      this.takvimVerileri = veriler; this.CD.detectChanges();
    } catch { /* sessiz */ }
  }

  async takvimOnceki() { if (this.takvimAy === 0) { this.takvimAy = 11; this.takvimYil--; } else this.takvimAy--; await this.takvimYukle(); }
  async takvimSonraki() { if (this.takvimAy === 11) { this.takvimAy = 0; this.takvimYil++; } else this.takvimAy++; await this.takvimYukle(); }

  async takvimGunSec(hucre: ITakvimHucre) {
    if (!hucre.ayIci) { this.takvimAy = hucre.tarih.getMonth(); this.takvimYil = hucre.tarih.getFullYear(); await this.takvimYukle(); }
    this.seciliGun = new Date(hucre.tarih); await this.yukle(); this.CD.detectChanges();
  }

  async buguneGit() {
    const b = new Date(); this.seciliGun = b;
    if (b.getMonth() !== this.takvimAy || b.getFullYear() !== this.takvimYil) { this.takvimAy = b.getMonth(); this.takvimYil = b.getFullYear(); await this.takvimYukle(); }
    await this.yukle(); this.CD.detectChanges();
  }

  private takvimGunGuncelle() {
    const key = this.seciliGunKey;
    if (this.aktifSatir) {
      this.takvimVerileri = { ...this.takvimVerileri, [key]: { toplamDk: this.aktifSatir.toplamDakika, durum: this.aktifSatir.durum, fazlaMesaiVar: this.aktifSatir.toplamDakika > this.aktifVardiyaSureDk } };
    } else {
      const v = { ...this.takvimVerileri }; delete v[key]; this.takvimVerileri = v;
    }
  }
}
